import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { EarconName, useEarcon } from '../../../hooks/useEarcon';
import { useRefArray } from '../../../hooks/useRefArray';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { findNextIndex } from '../../../util/findIndex';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { Icon } from '../../Icon';
import { TableRegions } from '../../Table';
import { Defaults } from '../../Table/__internal__/Defaults';
import { BaseTable, defaultSizes, hasSelectionColumn } from '../../Table/__internal__/internal';
import { GroupHeaderItem } from '../GroupHeaderItem';
import { Item } from '../Item';
import { BaseItem } from '../Item/__internal__/BaseItem';
import { LinkItem } from '../LinkItem';
import { useListState } from '../useListState';
import { useListContext } from './';
import { useGetInfoCell } from './hooks/ListNarration/useGetInfoCell';
import { useSayCell } from './hooks/ListNarration/useSayCell';
import { IconHelpPopup } from './IconHelpPopup';
import { IconMenuButton } from './IconMenuButton';
import { getCellHelpPopup, getCellIcon, getCellMenuButton } from './utils/utils';
import bundle from '../NovaComponents-List-gui-icu.properties';
import cnButton from '../../Button/__internal__/BaseButton.css.json';
import cnGroupHeaderItem from '../GroupHeaderItem/GroupHeaderItem.css.json';
import cnItem from '../Item/Item.css.json';
import cn from '../List.css.json';
import '../List.css';
import { jsx as _jsx } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);

// Minimum size needed for an icon button, normal icons will be centered.
const compactWidth = 33;

// Add space to the last cell for the scrollbar, this mimics what Table does when
// cell borders are not visible
const paddingInlineEnd = getScrollbarSize() + 1;

/**
 * @internal
 */
export const BaseList = props => {
  const localize = useTextLocalizer();
  const earconError = useEarcon({
    source: EarconName.Error
  });
  const selectionMode = !props.disabled ? props.selectionMode ?? 'none' : 'none';
  // pass a 1 to hasSelectionColumn for column count since List always has a column
  // (it doesn't matter if there are more columns...just need to know there's at least 1)
  const hasSelectionCol = hasSelectionColumn(selectionMode, 1, props.rowCount);
  const startIndex = hasSelectionCol ? 1 : 0;
  const currentPosition = useRef(props.initialFocus ?? {
    rowIndex: 0,
    colIndex: startIndex,
    region: TableRegions.Data
  });
  useEffect(() => {
    // consumer can update initialFocus anytime, match the value
    // to our currentPosition
    currentPosition.current = props.initialFocus;
  }, [props.initialFocus]);
  const [isActive, setIsActive] = useState(false);
  const [rowIndexPressed, setRowIndexPressed] = useState(null);
  const [rowIndexHelpPopupExpanded, setRowIndexHelpPopupExpanded] = useState(-1);
  const [focusIndicator, setFocusIndicator] = useState();
  const dt = useDesignTokens();
  const direction = useIsRTL() ? 'rtl' : 'ltr';
  const getItemProps = rowIndex => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return null;
    return props.getItemInfo?.(rowIndex)?.props ?? props.renderItem?.({
      index: rowIndex,
      itemKey: getRowIdentifier(rowIndex)
    })?.props ?? itemColumn?.item?.(rowIndex)?.props;
  };
  const getItemGroup = rowIndex => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return '';
    return getItemProps(rowIndex)?.group ?? '';
  };
  const listContext = useListContext();
  const internalRef = useRef(null);
  const narratorRef = props.narratorRef ?? useRef();

  // Determine from cell metadata or internal columns, the colspan of the
  // given row/col indices (default is 1).
  const colSpanValue = rowIndex => {
    // only on itemColumn
    const currentRefGrid = internalRef.current;
    const columnIndex = hasSelectionCol ? 1 : 0;
    const metadataCell = currentRefGrid?._getRowMetadata(rowIndex).getCell(columnIndex);
    if (metadataCell && metadataCell.colSpan > 1) return metadataCell.colSpan;
    const {
      colSpan,
      key: columnKey
    } = itemColumn ?? {};
    return typeof colSpan === 'function' ? colSpan({
      columnIndex,
      rowIndex,
      columnKey
    }) : colSpan ?? 1;
  };
  const iconColumnsWidth = props.iconColumns ? props.iconColumns.reduce((accumulator, col) => accumulator + (col.width ?? compactWidth), 0) + (listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0) : 0;
  const listWidth = props.width ?? defaultSizes.tableWidth;
  const selectionColumnWidth = startIndex ? defaultSizes.selectionControlColumnWidth : 0;
  const itemColumnWidth = listWidth - selectionColumnWidth - iconColumnsWidth;
  const getRowIdentifier = useCallback(rowIndex => {
    const rowKey = props.getRowKey;
    return rowKey ? rowKey(rowIndex) : Defaults.getRowKey(rowIndex);
  }, [props.getRowKey]);
  const itemColumn = useMemo(() => {
    // Table columns set the default maximum width to 1000. Since we only want the item column to resize
    // we're overriding the max column width to make sure the icon columns aren't resized.
    const column = props.itemColumn ? {
      ...props.itemColumn,
      width: itemColumnWidth,
      maximumWidth: 99999
    } : {
      key: 'itemColumn',
      width: props.width ? itemColumnWidth : undefined,
      maximumWidth: props.width ? 99999 : undefined
    };
    if (!props.renderItem) {
      column.cellContent = (_columnIndex, rowIndex, _columnKey, ref, disabled, readOnly) => column.item?.(rowIndex, ref, disabled, readOnly) ?? null;
    }
    const isItemLastColumn = !props.iconColumns || props.iconColumns?.length === 0;
    // const colSpan = props.renderItem ? 1 : colSpanValue(rowIndex)
    // const isSpannedItemLastColumn =
    // pass a 1 to hasSelectionColumn for column count since List always has a column
    // (it doesn't matter if there are more columns...just need to know there's at least 1)
    if (hasSelectionColumn(selectionMode, 1, props.rowCount) && !props.disabled) {
      column.style = {
        paddingInlineStart: 'var(--list-spacing-s)',
        paddingInlineEnd: isItemLastColumn ? 'calc(var(--table-last-column-pd-adjustment) + var(--list-spacing-s))' : 'var(--list-spacing-s)',
        ...column.style
      };
    }
    return column;
  }, [props.itemColumn, props.width, props.renderItem, props.iconColumns, props.rowCount, props.disabled, itemColumnWidth, selectionMode]);
  const getItemType = rowIndex => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return 'invalid';
    const type = props.getItemInfo?.(rowIndex)?.type ?? props.renderItem?.({
      index: rowIndex,
      itemKey: getRowIdentifier(rowIndex)
    })?.type ?? itemColumn?.item?.(rowIndex)?.type;
    if (type === Item || type === BaseItem) {
      return 'item';
    } else if (type === LinkItem) {
      return 'linkItem';
    } else if (type === GroupHeaderItem) {
      return 'groupItem';
    }
    return 'custom';
  };

  // Table Defaults of a row key based on row index. Refer to Table's Default.ts
  const getRowIndex = props.getRowIndex ?? (rowKey => {
    const parsedIndex = parseInt(rowKey);
    return isNaN(parsedIndex) ? -1 : parsedIndex;
  });
  const isItemReadOnly = rowKey => getItemType(getRowIndex(rowKey)) === 'groupItem' ? true : props.isItemReadOnly?.(rowKey) ?? false;
  if (currentPosition?.current) {
    // Unlike Table, the List does not have header so it's never -1. Otherwise,
    // -1 may have the focus disappeared.
    currentPosition.current.rowIndex = Math.max(0, Math.min(currentPosition.current.rowIndex, props.rowCount - 1));
  }
  const defaultTableRef = useRef(null);
  const tableRef = props.tableRef ?? defaultTableRef;
  const {
    refs: helpPopupAPIRefs,
    createSetRef: createHelpPopupAPIRef
  } = useRefArray();
  const {
    refs: helpPopupButtonRefs,
    createSetRef: createHelpPopupButtonRef
  } = useRefArray();
  const {
    refs: menuButtonAPIRefs,
    createSetRef: createMenuButtonAPIRef
  } = useRefArray();
  const {
    refs: menuButtonRefs,
    createSetRef: createMenuButtonRef
  } = useRefArray();
  const tableState = useListState({
    selectedRows: props.selectedRows,
    selectAll: props.selectAll
  });
  const scrollTo = props.scrollTo ?? tableState.scrollTo;
  const typeAheadToIndex = useCallback(i => {
    if (i !== -1) {
      const cell = {
        rowIndex: i,
        colIndex: currentPosition.current?.colIndex ?? 0,
        region: TableRegions.Data
      };
      scrollTo({
        cell,
        align: 'smart',
        focusToCell: true,
        focusIndicator: 'visible'
      });
    } else earconError.play();
  }, [earconError, scrollTo]);
  const delay = () => new Promise(resolve => setTimeout(() => resolve(), 0));
  const iconColumns = useMemo(() => {
    // sorting non-interactive icon columns on the left and interactive icon
    // columns, including HelpPopup, on the right
    const order = col => col.cellHelpPopup || col.cellMenuButton ? 2 : col.onPress ? 1 : 0;
    const iconColumns = (props.iconColumns ? [...props.iconColumns] : []).sort((iconColumn1, iconColumn2) => order(iconColumn1) - order(iconColumn2)).map((column, i) => {
      const isLastColumn = i === (props.iconColumns?.length ?? 0) - 1;
      const onPress = column.onPress;
      const col = {
        ...column,
        width: (column.width ?? compactWidth) + (isLastColumn && listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0),
        minimumWidth: (column.width ?? compactWidth) + (isLastColumn && listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0),
        alignment: 'center',
        preventToggleSelection: true,
        style: {
          paddingInlineStart: '0',
          paddingInlineEnd: isLastColumn ? 'var(--table-last-column-pd-adjustment)' : '0'
        },
        // only override onPress if used
        onPress: onPress ? (rowIndex, columnKey) => {
          if (!col.disabled?.(rowIndex) && !helpPopupAPIRefs[rowIndex]?.isOpen && !menuButtonAPIRefs[rowIndex]?.isOpen) onPress?.(rowIndex, columnKey);
        } : undefined,
        cellContent: (columnIndex, rowIndex, _columnKey, _ref, disabled, readOnly) => {
          const cellHelpPopupProps = getCellHelpPopup(col, rowIndex);
          const cellMenuButtonProps = getCellMenuButton(col, rowIndex);
          if (readOnly && (!cellHelpPopupProps && onPress || cellHelpPopupProps && !col?.displayHelpPopupReadOnly?.(rowIndex))) return null;
          if (cellHelpPopupProps) return /*#__PURE__*/_jsx("div", {
            className: cn.iconContent,
            children: /*#__PURE__*/_jsx(IconHelpPopup, {
              buttonRef: button => {
                setRef(col.helpPopupButtonRef?.(rowIndex), button);
                setRef(createHelpPopupButtonRef?.(rowIndex), button);
              },
              ref: createHelpPopupAPIRef(rowIndex),
              testId: postfixTestId(col.testId, `row${rowIndex}-helpPopup`),
              disabled: disabled || col.disabled?.(rowIndex),
              ...cellHelpPopupProps,
              className: cx(isActive && currentPosition.current?.rowIndex === rowIndex && currentPosition.current?.colIndex === columnIndex + startIndex ? cnButton.active : undefined, cellHelpPopupProps.className),
              onClose: () => {
                setRowIndexHelpPopupExpanded(-1);
                delay().then(() => narratorRef.current?.announcePosition());
              },
              onEscape: () => {
                if (currentPosition.current) {
                  scrollTo({
                    cell: currentPosition.current,
                    align: 'smart',
                    focusToCell: true,
                    focusIndicator: 'visible'
                  });
                }
              }
            })
          });
          if (cellMenuButtonProps) return /*#__PURE__*/_jsx("div", {
            className: cn.iconContent,
            children: /*#__PURE__*/_jsx(IconMenuButton, {
              ...cellMenuButtonProps,
              ref: createMenuButtonAPIRef?.(rowIndex),
              buttonRef: button => {
                setRef(col.menuButtonRef?.(rowIndex), button);
                setRef(createMenuButtonRef?.(rowIndex), button);
              },
              disabled: disabled || col.disabled?.(rowIndex),
              testId: postfixTestId(col.testId, `row${rowIndex}-menuButton`),
              variant: cellMenuButtonProps.variant ?? ButtonVariant.Icon,
              onDismiss: () => {
                tableRef.current?.focus();
                delay().then(() => narratorRef.current?.announcePosition());
              },
              className: cx(isActive && currentPosition.current?.rowIndex === rowIndex && currentPosition.current?.colIndex === columnIndex + startIndex ? cnButton.active : undefined, cn.iconMenuButton, cellMenuButtonProps.className)
            })
          });
          const cellIcon = getCellIcon(col, rowIndex);
          if (!cellIcon) {
            // null will prevent selection not showing so
            // using the following to have something empty,
            // which will show the selection background
            return /*#__PURE__*/_jsx(Fragment, {});
          }
          return onPress ? /*#__PURE__*/_jsx("div", {
            className: cn.iconContent,
            children: /*#__PURE__*/_jsx(Button, {
              ref: e => setRef(col.iconButtonRef?.(rowIndex), e),
              testId: postfixTestId(col.testId, `row${rowIndex}-cellIcon`),
              icon: cellIcon,
              tooltip: cellIcon.tooltip,
              ariaLabel: cellIcon.ariaLabel,
              style: {
                margin: '0 auto',
                ...cellIcon.style
              },
              variant: cellIcon.variant ?? ButtonVariant.Icon,
              className: cx(isActive && currentPosition.current?.rowIndex === rowIndex && currentPosition.current?.colIndex === columnIndex + startIndex ? cnButton.active : undefined, cellIcon.className),
              disabled: disabled || col.disabled?.(rowIndex),
              theme: () => props.tokens?.theme ? {
                '--button-fg': 'var(--list-item-fg)'
              } : undefined
            })
          }) : /*#__PURE__*/_jsx("div", {
            className: cn.iconContent,
            children: /*#__PURE__*/_jsx(Icon, {
              ...cellIcon,
              className: cx(cn.iconColumnStaticIcon, cellIcon.className),
              disabled: disabled || col.disabled?.(rowIndex),
              theme: () => {
                props.tokens?.theme ? {
                  '--icon-default-override-fg': 'var(--list-item-fg)'
                } : undefined;
              }
            })
          });
        }
      };
      return col;
    });
    return iconColumns;
  }, [props.iconColumns, props.tokens?.theme, listContext.tableHasVerticalScrollbar, helpPopupAPIRefs, menuButtonAPIRefs, createHelpPopupAPIRef, isActive, startIndex, createMenuButtonAPIRef, createHelpPopupButtonRef, narratorRef, scrollTo, createMenuButtonRef, tableRef]);
  const columns = [itemColumn, ...iconColumns];
  const listInfoCell = useGetInfoCell({
    getItemType,
    getItemProps,
    accessibilityInfo: props.accessibilityInfo,
    helpPopupButtonRefs,
    menuButtonRefs,
    readOnly: props.readOnly,
    isItemReadOnly,
    getRowKey: getRowIdentifier
  });
  const listSayCell = useSayCell({
    columns,
    startColIndex: startIndex,
    getItemType,
    getItemProps,
    getItemGroup,
    disabled: props.disabled,
    isItemDisabled: props.isItemDisabled,
    narration: props.narration,
    readOnly: props.readOnly,
    isItemReadOnly,
    getRowKey: getRowIdentifier
  });

  // using onActivate is not supported when using link items
  const onActivate = getItemType(currentPosition.current?.rowIndex ?? 0) === 'linkItem' ? undefined : props.onActivate;
  const tokens = {
    ...getListDesignTokens(dt, {
      tokens: props.tokens
    }),
    ...props.theme?.(dt)
  };
  const selectedCellValues = useMemo(() => {
    const overrides = props.tokens?.theme?.({
      state: 'selected'
    });
    const {
      fg,
      bg
    } = overrides || {};
    let selectedItemfg;
    if (props.selectionMode === 'single' || props.selectionMode === 'desktop') selectedItemfg = dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-text-subtle-normal'] : dt.tokens['color-action-text-subtle-selected'];
    return renderDesignTokens({
      fg: fg ? 'var(--list-item-selected-fg)' : selectedItemfg,
      'description-fg': fg ? 'var(--list-item-selected-fg)' : selectedItemfg,
      bg: bg && 'var(--list-item-selected-bg)',
      'hover-bg': bg && 'var(--list-item-selected-bg)'
    }, 'list-item');
  }, [dt.themeVariant, dt.tokens, props.selectionMode, props.tokens]);

  // custom row rendering function
  const renderListRow = rowProps => {
    const {
      rowIndex,
      createRow,
      createCell,
      hasSelectionControl,
      isEmpty,
      disabled,
      readOnly,
      dragging
    } = rowProps;
    const isGroupItem = getItemType(rowIndex) === 'groupItem';
    const preference = props.preference ?? tableState.preference;
    const selectedCell = dragging !== true && (!disabled && preference?.selectAll === true || !!preference?.selectedRows?.has(getRowIdentifier(rowIndex)));

    // do not render an empty row...this will cause exceptions
    if (isEmpty) return createRow({
      children: undefined
    });

    // construct regular rows
    const cells = [];
    let i = 0;
    if (hasSelectionControl) {
      // if this is not a group header item, add a selection cell
      !isGroupItem && cells.push(rowProps.createSelectionCell(i));
      // there is a selection column, so increment the column index
      i++;
    }
    const item = props.renderItem?.({
      index: rowIndex,
      itemKey: getRowIdentifier(rowIndex),
      disabled,
      readOnly
    }) ?? itemColumn.cellContent;

    // create group headers for group rows
    if (isGroupItem) {
      const cell = createCell({
        index: 0,
        content: item,
        colSpan: (hasSelectionControl ? 1 : 0) + columns.length,
        className: cnGroupHeaderItem.groupItem
      });

      // return a row with only the header cell
      return createRow({
        children: cell
      });
    }
    let colSpan = props.renderItem ? 1 : colSpanValue(rowIndex);

    // add content cell
    cells.push(rowProps.createCell({
      // pass in i for the column index and then increment i
      // for any additional column below
      index: i++,
      content: item,
      className: cx(cn.cell, cn.direction[direction]),
      colSpan,
      style: {
        paddingInlineEnd: colSpan > 1 && colSpan === iconColumns.length + 1 ? 'var(--table-last-column-pd-adjustment)' : '0'
      }
    }));

    // add any additional cells
    const itemColumnIndexOffset = i;
    for (i = Math.max(i, rowProps.columnStartIndex); i <= rowProps.columnStopIndex; i++) {
      // create icon button for given column
      colSpan--;
      if (colSpan > 0) continue;
      const iconColumn = iconColumns?.[i - itemColumnIndexOffset];
      const colIndex = hasSelectionControl ? i - 1 : i;
      if (iconColumn) {
        // add icon button cell
        cells.push(rowProps.createCell({
          index: i,
          content: iconColumn.cellContent?.(colIndex, rowIndex, iconColumn.key, undefined, disabled, readOnly),
          className: cx(cn.cell, cn.direction[direction], iconColumn.displayOnDemand && cn.displayIconsOnDemand)
        }));
      }
    }
    const onDemandFocusClass = focusIndicator === 'visible' && currentPosition.current?.rowIndex === rowIndex ? cn.focusedShowIcons : undefined;
    const helpPopupByRowClass = rowIndexHelpPopupExpanded === -1 ? undefined : rowIndexHelpPopupExpanded === rowIndex ? cn.helpPopupExpandedByRow : cn.ignoreHover;

    // construct a row around cell content and return to table for display
    const row = createRow({
      children: cells,
      className: cx(props.borderHighlighting && cn.rowSelectionBorder, (selectedCell || rowIndexPressed === rowIndex) && props.borderHighlighting && cn.rowSelectionBorderSelected, cn.itemRow, onDemandFocusClass, helpPopupByRowClass),
      // Don't render background styling when using borderHighlighting
      style: {
        ...(selectedCell && !hasSelectionControl && !props.borderHighlighting && renderDesignTokens({
          'description-fg': dt.tokens['color-text-neutral']
        }, 'list-item')),
        ...(selectedCell && selectedCellValues),
        ...(disabled && renderDesignTokens({
          fg: 'var(--list-item-disabled-fg)'
        }, 'list-item')),
        ...((selectedCell || rowIndexPressed === rowIndex) && props.borderHighlighting && renderDesignTokens({
          'pressed-bg': 'transparent',
          'pressed-filter': 'none',
          'selected-bg': 'transparent',
          'selected-filter': 'none',
          'selected-inactive-bg': 'transparent',
          'selected-inactive-filter': 'none'
        }, 'table'))
      }
    });
    return row;
  };
  if (currentPosition?.current) {
    // the visibility of the selection column can be change via disabled so account for
    // the out of range focus position.
    currentPosition.current.colIndex = Math.max(0, Math.min(currentPosition.current.colIndex, columns.length - 1 + (hasSelectionCol ? 1 : 0)));
  }
  let deleteRowMessage;
  if (!props.disabled && !props.readOnly && props.onDeletePress) {
    deleteRowMessage = props.deleteRowAnnouncement ?? localize('deleteToDelete.msg');
  }
  return /*#__PURE__*/_jsx("div", {
    className: cx(cn.list, props.disabled && cnItem.disabled, props.className),
    style: tokens,
    children: /*#__PURE__*/_jsx("div", {
      className: cx(cn.listInner, props.className ? `${props.className}-inner` : undefined),
      onMouseDown: e => {
        const target = e.target;
        const row = target.closest('[role="row"]');
        if (row) {
          const rowIndex = currentPosition.current?.rowIndex ?? 0;
          setRowIndexPressed(rowIndex);
        }
      },
      onMouseUp: () => {
        setRowIndexPressed(null);
      },
      onClick: e => {
        const rowIndex = currentPosition.current?.rowIndex ?? 0;
        const colIndex = currentPosition.current?.colIndex ?? 0;
        const rowKey = getRowIdentifier(rowIndex);
        const disabled = props.disabled || props.isItemDisabled?.(rowKey);
        const readOnly = props.readOnly || isItemReadOnly?.(rowKey);
        const iconColumn = iconColumns.length > 0 && iconColumns[colIndex - 1 - startIndex];
        if (disabled) return;
        if (helpPopupButtonRefs[rowIndex]?.contains(e.target)) {
          if (iconColumn && iconColumn.cellHelpPopup) {
            const displayReadOnly = readOnly && iconColumn?.displayHelpPopupReadOnly?.(rowIndex);
            if (!readOnly || displayReadOnly) {
              const columnKey = iconColumn?.key || '';
              const isOpen = helpPopupAPIRefs[rowIndex]?.toggle?.();
              // Table does not fire onPress in readOnly so account for displayReadOnly
              if (displayReadOnly) iconColumn.onPress?.(rowIndex, columnKey);
              setRowIndexHelpPopupExpanded(isOpen ? rowIndex : -1);
            }
          }
        } else {
          helpPopupAPIRefs[rowIndex]?.close?.();
          setRowIndexHelpPopupExpanded(-1);
        }
      },
      onKeyDown: e => {
        const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
        if (hasModifier) return;
        const rowIndex = currentPosition.current?.rowIndex ?? 0;
        const rowKey = getRowIdentifier(rowIndex);
        const disabled = props.disabled || props.isItemDisabled?.(rowKey);
        const readOnly = props.readOnly || isItemReadOnly?.(rowKey);
        if (!props.disabled && props.rowCount && e.key.trim().length === 1 && itemColumn?.containsInteractiveContent !== true) {
          const startRowIndex = Math.min(rowIndex + 1, props.rowCount - 1);
          if (!props.loadData) {
            if (props.getNextMatchedItem !== undefined) {
              const i = props.getNextMatchedItem(e.key, startRowIndex);
              if (i !== undefined) {
                typeAheadToIndex(i);
                e.stopPropagation();
                e.preventDefault();
              }
            } else {
              const key = e.key.toLowerCase();
              typeAheadToIndex(findNextIndex({
                length: props.rowCount,
                start: startRowIndex,
                isValidIndex: index => {
                  if (props.disabled || props.isItemDisabled?.(getRowIdentifier(index))) return false;
                  const itemType = getItemType(index);
                  if (itemType === 'item') return !!getItemProps(index).title.toLowerCase().startsWith(key);
                  if (itemType === 'linkItem') {
                    const linkItemProps = getItemProps(index);
                    // only string, if nodes, then consumer should use getNextMatchedItem instead
                    if (typeof linkItemProps.children === 'string') return linkItemProps.children.toLowerCase().startsWith(key);
                  }
                  return false;
                },
                wrap: false
              }) ?? -1);
              e.stopPropagation();
              e.preventDefault();
            }
          }
        }
        if (disabled || readOnly) return;
        setIsActive(((e.key === 'Enter' || e.key === ' ') && props.iconColumns?.some(col => (!!col.onPress || !!col.cellHelpPopup || !!col.cellMenuButton) && !col.disabled?.(rowIndex))) ?? false);
        if (e.key === 'Enter' || e.key === ' ') {
          setRowIndexPressed(rowIndex);
        }
        if (e.key === 'Enter' && (currentPosition.current?.colIndex ?? startIndex) === startIndex) {
          const itemProps = getItemProps(rowIndex);
          if (itemProps && getItemType(rowIndex) === 'linkItem' && itemProps.onPress) {
            e.stopPropagation();
            e.preventDefault();
            itemProps.onPress(e);
          }
        }
      },
      onKeyUp: e => {
        const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
        if (hasModifier) return;
        const rowIndex = currentPosition.current?.rowIndex ?? 0;
        const colIndex = currentPosition.current?.colIndex ?? 0;
        const rowKey = getRowIdentifier(rowIndex);
        const disabled = props.disabled || props.isItemDisabled?.(rowKey);
        const readOnly = props.readOnly || isItemReadOnly?.(rowKey);
        const iconColumn = iconColumns.length > 0 && iconColumns[colIndex - 1 - startIndex];
        if (iconColumn && iconColumn.cellHelpPopup) {
          const displayReadOnly = readOnly && iconColumn?.displayHelpPopupReadOnly?.(rowIndex);
          if (!disabled && (!readOnly || displayReadOnly) && (e.key === 'Enter' || e.key === ' ')) {
            const columnKey = iconColumn?.key || '';
            helpPopupAPIRefs[rowIndex]?.open?.();
            // Table does not fire onPress in readOnly so account for displayReadOnly
            if (displayReadOnly) iconColumn.onPress?.(rowIndex, columnKey);
          }
        }
        if (disabled || readOnly) return;
        if ((e.key === 'Enter' || e.key === ' ') && iconColumn?.cellMenuButton) {
          menuButtonAPIRefs[rowIndex]?.toggle();
        }
        if (e.key === 'Enter' || e.key === ' ') {
          setRowIndexPressed(null);
        }
        if ((e.key === 'Enter' || e.key === ' ') && isActive) {
          setIsActive(false);
        }
        if (e.key === 'Delete') {
          props.onDeletePress?.(tableState.preference.selectedRows, currentPosition.current?.rowIndex ?? 0);
        }
      },
      children: /*#__PURE__*/_jsx(BaseTable, {
        ...tableState,
        ...props,
        tableRef: tableRef,
        internalRef: internalRef,
        narratorRef: narratorRef,
        theme: () => {
          return {
            '--scrollbar-hover-track-bg': 'transparent',
            ...props.tableTheme?.(dt)
          };
        },
        columns: columns,
        hideSelectedStateStyles: hasSelectionCol,
        hideHoverStateStyles: hasSelectionCol && !onActivate,
        hidePressedStateStyles: hasSelectionCol && !onActivate,
        renderRow: renderListRow,
        isRowDisabled: props.isItemDisabled,
        isRowReadOnly: isItemReadOnly,
        reorderRow: (fromRow, toRow, rowsToMove) => props.reorderItem?.(fromRow, toRow, rowsToMove),
        initialFocus: props.initialFocus ?? (props.preference?.scrollPosition ? undefined : {
          rowIndex: currentPosition.current?.rowIndex ?? 0,
          colIndex: currentPosition.current?.colIndex ?? startIndex,
          region: TableRegions.Data
        }),
        initialFocusVisible: props.initialFocusVisible ?? true,
        selectionMode: selectionMode,
        disableAutoFocus: props.disableAutoFocus ?? true,
        hideManageColumnsButton: true,
        cellBordersVisible: false,
        columnHeadersVisible: false,
        frozenColumnCount: 0,
        rowHeight: rowIndex => {
          if (getItemType(rowIndex) === 'groupItem') return 33;
          const itemHeight = props.itemHeight?.(rowIndex);
          if (typeof itemHeight === 'number') return itemHeight;
          return itemHeight === 'double' || itemHeight === 'mixed' ? 56 : 24;
        },
        showSeparator: index => {
          if (props.showSeparator !== undefined) return typeof props.showSeparator === 'function' ? props.showSeparator(index) : props.showSeparator;
          const titleMaxLines = getItemType(0) === 'item' ? getItemProps(0).titleMaxLines : undefined;
          const maxLines = getItemType(0) === 'linkItem' ? getItemProps(0).maxLines : undefined;
          return props.rowCount > 0 && (props.itemHeight?.(0) === 'double' || props.itemHeight?.(0) === 'mixed' || titleMaxLines > 1 || maxLines > 1);
        },
        hideFillRows: true,
        deleteRowAnnouncement: deleteRowMessage,
        onFocusChange: position => {
          currentPosition.current = position;
          props.onFocusChange?.(position);
        },
        onFocusIndicator: setFocusIndicator,
        onSelect: (selectedRows, selectAll, lastSelectedRowKey, selected) => {
          // "mixed" will never be used, not sure if it should be undefined or boolean
          props.onSelect?.(selectedRows, selectAll !== 'mixed' ? selectAll : false, lastSelectedRowKey, selected !== 'mixed' ? selected : undefined);
        },
        onActivate: onActivate,
        accessibilityInfo: {
          getCellInfo: listInfoCell
        },
        narration: {
          onSayCell: listSayCell,
          onSaySelectionCell: listSayCell
        },
        ariaRoleDescription: props.ariaRoleDescription ? props.ariaRoleDescription : columns.length === 1 && !hasSelectionCol && (getItemType(0) === 'item' || getItemType(0) === 'groupItem' && getItemType(1) === 'item') ? 'listbox' : undefined,
        ariaDescribedBy: props.ariaDescribedBy,
        cellBackground: params => props.cellBackground?.(params) ?? (tableState.preference.selectedRows?.has(`${params.rowIndex}`) ? 'var(--list-item-selected-bg)' : 'var(--list-item-bg)'),
        ignoreTableOverrideTheme: true
      })
    })
  });
};
export const getListDesignTokens = (dt, opts) => {
  const overrides = opts?.tokens?.theme;
  const normalTokens = overrides?.({
    state: 'normal'
  });
  return {
    ...renderDesignTokens({
      m: dt.tokens['space-base-m'],
      s: dt.tokens['space-base-s'],
      xs: dt.tokens['space-base-xs'],
      xxs: dt.tokens['space-base-xxs'],
      l: dt.tokens['space-base-l'],
      'squish-s': dt.tokens['space-squish-s']
    }, 'list-spacing'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-subtle-normal']
    }, 'list-item-default'),
    ...renderDesignTokens({
      fg: dt.tokens['color-action-text-subtle-normal'],
      bg: dt.tokens['color-container-background-0'],
      'description-fg': dt.tokens['color-label-text'],
      'disabled-fg': dt.tokens['color-action-text-subtle-disabled'],
      // the following is also used for useItemHeight
      'font-size-m': opts?.tokens?.font?.fontSize ?? dt.tokens['font-size-body-text-m'],
      'font-size-s': opts?.tokens?.font?.fontSize ?? dt.tokens['font-size-body-text-s'],
      'font-size-xs': opts?.tokens?.font?.fontSize ?? dt.tokens['font-size-body-text-xs'],
      'text-decoration': opts?.tokens?.font?.textDecoration ?? 'inherit',
      'font-weight-default': opts?.tokens?.font?.fontWeight ?? dt.tokens['font-style-weight-400'],
      'font-weight-thick': opts?.tokens?.font?.fontWeight ?? dt.tokens['font-style-weight-700'],
      'font-style': opts?.tokens?.font?.fontStyle ?? 'inherit',
      'font-family': opts?.tokens?.font?.fontFamily ?? 'inherit',
      'unread-bg': dt.tokens['color-status-icon-info']
    }, 'list-item'),
    ...renderDesignTokens({
      bd: dt.tokens['color-container-border-4'],
      fg: opts?.tokens?.theme?.({
        state: 'normal'
      })?.fg ?? dt.tokens['color-action-text-subtle-normal']
    }, 'list-group-header'),
    ...renderDesignTokens({
      'border-highlighting-color': dt.tokens['color-action-border-standard-selected']
    }, 'list-row'),
    ...(opts?.tokens?.whiteSpace ? renderDesignTokens({
      'white-space': opts?.tokens?.whiteSpace
    }, 'list-item') : {}),
    ...(overrides ? {
      ...renderDesignTokens(overrides({
        state: 'normal'
      }) ?? {}, 'list-item'),
      ...renderDesignTokens({
        fg: overrides({
          state: 'normal'
        })?.fg
      }, 'list-item-default'),
      ...renderDesignTokens(overrides({
        state: 'selected'
      }) ?? {}, 'list-item-selected'),
      ...renderDesignTokens(overrides({
        state: 'hover'
      }) ?? {}, 'list-item-hover')
    } : {}),
    ...(normalTokens?.fg ? {
      ...renderDesignTokens({
        fg: 'var(--list-item-default-fg)'
      }, 'list-item-default-description'),
      ...renderDesignTokens({
        fg: 'var(--list-item-default-fg)'
      }, 'list-item-description')
    } : {}),
    ...(!dt.enableNovaTokens && getListLegacyDesignTokens(dt, opts))
  };
};
export const getListLegacyDesignTokens = (dt, opts) => {
  let listItemTokens = {};
  switch (dt.themeVariant) {
    case 'high-contrast':
      listItemTokens = {
        fg: dt.c.white,
        'description-fg': dt.c.white
      };
      break;
    case 'dark':
      listItemTokens = {
        fg: dt.c.white,
        'description-fg': dt.c.white80
      };
      break;
    default:
      listItemTokens = {
        fg: dt.c.n_3,
        'description-fg': dt.c.n_1
      };
  }
  const overrides = opts?.tokens?.theme;
  const normalTokens = overrides?.({
    state: 'normal'
  });
  return {
    ...renderDesignTokens({
      ...listItemTokens
    }, 'list-item-default'),
    ...renderDesignTokens({
      'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
      // the following is also used for useItemHeight
      'font-size-m': opts?.tokens?.font?.fontSize ?? dt.reportTokens.font.size.m,
      'font-size-s': opts?.tokens?.font?.fontSize ?? dt.reportTokens.font.size.s,
      'font-size-xs': opts?.tokens?.font?.fontSize ?? dt.reportTokens.font.size.xs,
      'text-decoration': opts?.tokens?.font?.textDecoration ?? 'inherit',
      'font-weight-default': opts?.tokens?.font?.fontWeight ?? dt.reportTokens.font.weight.default,
      'font-weight-thick': opts?.tokens?.font?.fontWeight ?? dt.reportTokens.font.weight.thick,
      'font-style': opts?.tokens?.font?.fontStyle ?? 'inherit',
      'font-family': opts?.tokens?.font?.fontFamily ?? 'inherit',
      'unread-bg': dt.c.primary,
      ...listItemTokens
    }, 'list-item'),
    ...renderDesignTokens({
      bd: dt.tokens['color-container-border-4'],
      fg: opts?.tokens?.theme?.({
        state: 'normal'
      })?.fg ?? dt.tokens['color-body-text']
    }, 'list-group-header'),
    ...renderDesignTokens({
      'border-highlighting-color': dt.themeVariant === 'dark' ? dt.reportTokens.stateStandard.selected.bd : dt.tokens['color-neutral']
    }, 'list-row'),
    ...(opts?.tokens?.whiteSpace ? renderDesignTokens({
      'white-space': opts?.tokens?.whiteSpace
    }, 'list-item') : {}),
    ...(overrides ? {
      ...renderDesignTokens(overrides({
        state: 'normal'
      }) ?? {}, 'list-item'),
      ...renderDesignTokens({
        fg: overrides({
          state: 'normal'
        })?.fg
      }, 'list-item-default'),
      ...renderDesignTokens(overrides({
        state: 'selected'
      }) ?? {}, 'list-item-selected'),
      ...renderDesignTokens(overrides({
        state: 'hover'
      }) ?? {}, 'list-item-hover')
    } : {}),
    ...(normalTokens?.fg ? {
      ...renderDesignTokens({
        fg: 'var(--list-item-default-fg)'
      }, 'list-item-default-description'),
      ...renderDesignTokens({
        fg: 'var(--list-item-default-fg)'
      }, 'list-item-description')
    } : {})
  };
};
//# sourceMappingURL=BaseList.js.map